<template>
  <div class="number-card">
    <!-- ── Light stem (mirrors ShortTextCard): section label, title,
         optional one line, optional one image. ── -->
    <div class="question-block">
      <p v-if="sectionLabel" class="section-label">{{ sectionLabel }}</p>
      <h1 class="question-title">{{ title }}</h1>
      <p v-if="prompt" class="question-prompt">{{ prompt }}</p>
    </div>

    <div v-if="imageId != null" class="stem-image-slot">
      <img
        v-if="imageRecord"
        class="stem-image"
        :src="imageUrl(imageRecord)"
        :alt="altTextFor(imageRecord, locale)"
        @error="onImgError($event, imageRecord, locale)"
      >
      <div v-else class="stem-image-placeholder">
        <span class="stem-image-icon" aria-hidden="true">🖼</span>
        <span class="stem-image-text">{{ dict.imagePlaceholder }} #{{ imageId }}</span>
      </div>
    </div>

    <!-- ── Input: a typed numeric field with a unit suffix. NOT the
         +/− stepper (that's numeric_stepper). ── -->
    <div class="input-block">
      <div class="number-field" :class="{ 'is-invalid': showError }">
        <input
          ref="input"
          type="text"
          class="number-input"
          :inputmode="inputMode"
          :value="rawValue"
          :placeholder="placeholder"
          :aria-invalid="showError ? 'true' : 'false'"
          :aria-describedby="showError ? errorId : undefined"
          autocomplete="off"
          @input="onInput"
          @blur="touched = true"
        >
        <span v-if="unit" class="number-unit">{{ unit }}</span>
      </div>
      <div class="input-footer">
        <p v-if="showError" :id="errorId" class="input-error" role="alert">{{ errorMessage }}</p>
        <span v-else-if="rangeHint" class="input-hint">{{ rangeHint }}</span>
      </div>
    </div>
  </div>
</template>

<script>
// NumberCard — runtime card for question_type = 'number'.
//
// Engine contract (docs/CONTRACT_CARD_ENGINE.md):
//   props : item, state
//   emits : state-changed   (no request-advance — a typed field must not
//                            auto-advance out from under the user)
//
// State shape: { value: <number> | null }. Emitted on every input — the
// PARSED number (Swiss/European separators normalized), or null when the
// field is empty or not yet a number. Persistence, when it lands, reads
// state.value straight into seq_answers.value_number (numeric) — distinct
// from numeric_stepper, which uses the smallint `value` column.
//
// Content shape (item.item.content):
//   title:    { de,en,fr,it }
//   prompt:   { de,en,fr,it }   optional one line
//   image_id: number | null     optional single stem image
//   input: { required, min, max, decimals, unit ({de,en,fr,it}) }
//
// Validity is computed by the shared evaluateNumber() helper so the card's
// inline feedback and the engine's _isAnswered()/gating use one source of
// truth.

import i18n from '@/i18n';
import { imageUrl, altTextFor, onImgError } from '@/services/imageHelpers';
import { loadImageMap } from '@/services/imageApi';
import { formatSectionLabel } from '../sectionLabel.js';
import { numberConfig, evaluateNumber } from './numberValidation.js';

const DICT = {
  en: { imagePlaceholder: 'Image', errRequired: 'This field is required.', errNumber: 'Enter a number.', errDecimals: 'Too many decimal places.', errMin: 'Must be at least', errMax: 'Must be at most', range: 'Enter a value', between: 'between', and: 'and', upTo: 'up to', from: 'from', decimalsNote: 'decimal places' },
  de: { imagePlaceholder: 'Bild', errRequired: 'Dieses Feld ist erforderlich.', errNumber: 'Bitte eine Zahl eingeben.', errDecimals: 'Zu viele Nachkommastellen.', errMin: 'Mindestens', errMax: 'Höchstens', range: 'Wert eingeben', between: 'zwischen', and: 'und', upTo: 'bis', from: 'ab', decimalsNote: 'Nachkommastellen' },
  fr: { imagePlaceholder: 'Image', errRequired: 'Ce champ est obligatoire.', errNumber: 'Saisis un nombre.', errDecimals: 'Trop de décimales.', errMin: 'Au moins', errMax: 'Au plus', range: 'Saisir une valeur', between: 'entre', and: 'et', upTo: "jusqu'à", from: 'à partir de', decimalsNote: 'décimales' },
  it: { imagePlaceholder: 'Immagine', errRequired: 'Questo campo è obbligatorio.', errNumber: 'Inserisci un numero.', errDecimals: 'Troppi decimali.', errMin: 'Almeno', errMax: 'Al massimo', range: 'Inserisci un valore', between: 'tra', and: 'e', upTo: 'fino a', from: 'da', decimalsNote: 'decimali' },
};

export default {
  name: 'NumberCard',
  props: {
    item: { type: Object, required: true },
    state: { type: [Object, null], default: null },
  },
  emits: ['state-changed'],
  data() {
    return {
      // The raw text the user is typing. Kept locally because the parsed
      // number can't reconstruct in-progress input (e.g. "12," or "1'2").
      // Seeded from any prior state on mount.
      rawValue: this._initialRaw(),
      touched: false,
      imageMap: null,
    };
  },
  created() {
    if (this.imageId != null) {
      loadImageMap()
        .then((map) => { this.imageMap = map; })
        .catch(() => { /* leave the placeholder if the library can't load */ });
    }
  },
  computed: {
    content() {
      return (this.item && this.item.item && this.item.item.content) || {};
    },
    locale() {
      return i18n.global.locale.value;
    },
    dict() {
      return DICT[this.locale] || DICT.en;
    },
    config() {
      return numberConfig(this.item);
    },
    title() { return this._localised(this.content.title); },
    prompt() { return this._localised(this.content.prompt); },
    unit() { return this._localised(this.config.unit); },
    sectionLabel() { return formatSectionLabel(this.item, this.locale); },
    placeholder() {
      // A light example based on configured decimals (e.g. "0" or "0.0").
      const d = this.config.decimals;
      return d > 0 ? `0.${'0'.repeat(d)}` : '0';
    },
    inputMode() {
      return this.config.decimals > 0 ? 'decimal' : 'numeric';
    },
    imageId() {
      const v = Number(this.content.image_id);
      return Number.isFinite(v) ? v : null;
    },
    imageRecord() {
      if (this.imageId == null || !this.imageMap) return null;
      return this.imageMap.get(this.imageId) || null;
    },
    evaluation() {
      return evaluateNumber(this.rawValue, this.config);
    },
    showError() {
      return this.touched && this.evaluation.error != null;
    },
    errorMessage() {
      const d = this.dict;
      switch (this.evaluation.error) {
        case 'required': return d.errRequired;
        case 'not_number': return d.errNumber;
        case 'decimals': return `${d.errDecimals} (${this.config.decimals} ${d.decimalsNote})`;
        case 'min': return `${d.errMin} ${this._fmt(this.config.min)}${this.unit ? ' ' + this.unit : ''}.`;
        case 'max': return `${d.errMax} ${this._fmt(this.config.max)}${this.unit ? ' ' + this.unit : ''}.`;
        default: return '';
      }
    },
    rangeHint() {
      const { min, max } = this.config;
      const d = this.dict;
      const u = this.unit ? ` ${this.unit}` : '';
      if (min != null && max != null) return `${d.range} ${d.between} ${this._fmt(min)} ${d.and} ${this._fmt(max)}${u}`;
      if (min != null) return `${d.range} ${d.from} ${this._fmt(min)}${u}`;
      if (max != null) return `${d.range} ${d.upTo} ${this._fmt(max)}${u}`;
      return '';
    },
    errorId() {
      return `number-error-${this.item ? this.item.id : 'x'}`;
    },
  },
  methods: {
    _initialRaw() {
      const v = this.state && this.state.value;
      return typeof v === 'number' && Number.isFinite(v) ? String(v) : '';
    },
    _fmt(n) {
      // Display numbers with the active locale's decimal separator so the
      // hint/error reads naturally (12.5 → "12,5" in de/fr/it).
      if (typeof n !== 'number') return '';
      return n.toLocaleString(this.locale, { maximumFractionDigits: 4, useGrouping: false });
    },
    _localised(field) {
      if (!field || typeof field !== 'object') return '';
      const order = [this.locale, 'en', 'de', 'fr', 'it'];
      for (const loc of order) {
        const v = field[loc];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return '';
    },
    onInput(e) {
      this.rawValue = e.target.value;
      // Emit the parsed number (or null). The engine stores it verbatim;
      // validity is reported separately and gates advance.
      this.$emit('state-changed', { value: this.evaluation.value });
    },
    imageUrl,
    altTextFor,
    onImgError,
  },
};
</script>

<style scoped>
.number-card { font-family: inherit; }

.question-block { margin-bottom: 1.25rem; }
.section-label { font-size: 13px; color: #5f5e5a; margin: 0 0 6px; }
.question-title {
  font-size: 22px; font-weight: 500; margin: 0 0 8px;
  line-height: 1.25; color: #2c2c2a;
}
.question-prompt { font-size: 14px; color: #5f5e5a; margin: 0; }

.stem-image-slot { margin: 0 0 1.25rem; }
.stem-image {
  display: block; width: 100%; height: auto;
  border-radius: 8px; border: 0.5px solid rgba(0, 0, 0, 0.1);
}
.stem-image-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; height: 140px;
  border: 0.5px dashed rgba(0,0,0,0.18); border-radius: 8px;
  background: #fafaf7; color: #888780; font-size: 12px;
}
.stem-image-icon { font-size: 28px; line-height: 1; }

.input-block { margin-bottom: 1.25rem; }
.number-field {
  display: flex; align-items: stretch;
  border: 0.5px solid rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.number-field:focus-within {
  border-color: #534AB7;
  box-shadow: 0 0 0 3px rgba(83, 74, 183, 0.12);
}
.number-field.is-invalid { border-color: #C8392E; }
.number-field.is-invalid:focus-within { box-shadow: 0 0 0 3px rgba(200, 57, 46, 0.12); }

.number-input {
  flex: 1 1 auto; min-width: 0;
  padding: 12px 14px;
  font-size: 16px; font-family: inherit; color: #2c2c2a;
  border: 0; outline: none; background: transparent;
}
.number-input::placeholder { color: #b3b1a8; }

.number-unit {
  display: flex; align-items: center;
  padding: 0 14px;
  background: #f1efe8; color: #5f5e5a;
  font-size: 14px; font-weight: 500;
  border-left: 0.5px solid rgba(0, 0, 0, 0.12);
  white-space: nowrap;
}

.input-footer { margin-top: 6px; min-height: 16px; }
.input-error { font-size: 12px; color: #C8392E; margin: 0; }
.input-hint { font-size: 12px; color: #888780; }
</style>
